import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { Castle, Ruler, Plus, Pencil, Trash2, Euro, PartyPopper, Sparkles, AlertCircle, Upload, X, Image as ImageIcon, Power, Ban, CalendarOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StructuresPage() {
  const [structures, setStructures] = useState([]);
  const [offers, setOffers] = useState([]);
  const [options, setOptions] = useState([]);
  const [closurePeriods, setClosurePeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("structures");
  
  // Modal states
  const [structureModal, setStructureModal] = useState({ open: false, mode: "create", data: null });
  const [offerModal, setOfferModal] = useState({ open: false, mode: "create", data: null });
  const [optionModal, setOptionModal] = useState({ open: false, mode: "create", data: null });
  const [closureModal, setClosureModal] = useState({ open: false, mode: "create", data: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [structuresRes, offersRes, optionsRes, closuresRes] = await Promise.all([
        fetch(`${API}/catalogue/structures`, { credentials: "include" }),
        fetch(`${API}/catalogue/offers`, { credentials: "include" }),
        fetch(`${API}/catalogue/options`, { credentials: "include" }),
        fetch(`${API}/catalogue/closure-periods`, { credentials: "include" }),
      ]);
      
      setStructures(await structuresRes.json());
      setOffers(await offersRes.json());
      setOptions(await optionsRes.json());
      setClosurePeriods(await closuresRes.json());
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get full image URL
  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleToggleAvailability = async (structureId) => {
    try {
      const res = await fetch(`${API}/catalogue/structures/${structureId}/toggle-availability`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const result = await res.json();
      setStructures(prev => prev.map(s => s.id === structureId ? { ...s, available: result.available } : s));
      toast.success(result.available ? "Structure disponible" : "Structure indisponible");
    } catch {
      toast.error("Erreur lors du changement de disponibilité");
    }
  };

  const handleBulkAvailability = async (available) => {
    try {
      const res = await fetch(`${API}/catalogue/structures/bulk-availability`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ available }),
      });
      if (!res.ok) throw new Error();
      const result = await res.json();
      setStructures(prev => prev.map(s => ({ ...s, available })));
      toast.success(available ? `${result.updated_count} structures ouvertes` : `${result.updated_count} structures fermées`);
    } catch {
      toast.error("Erreur lors du changement groupé");
    }
  };

  const handleSaveClosure = async (formData) => {
    try {
      const isEdit = closureModal.mode === "edit";
      const url = isEdit ? `${API}/catalogue/closure-periods/${closureModal.data.id}` : `${API}/catalogue/closure-periods`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur");
      }
      toast.success(isEdit ? "Période modifiée" : "Période de fermeture créée");
      setClosureModal({ open: false, mode: "create", data: null });
      fetchData();
    } catch (e) {
      toast.error(e.message || "Erreur lors de la sauvegarde");
    }
  };

  const handleDeleteClosure = async (id) => {
    try {
      const res = await fetch(`${API}/catalogue/closure-periods/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      toast.success("Période supprimée");
      fetchData();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Structure CRUD
  const handleSaveStructure = async (formData) => {
    try {
      const isEdit = structureModal.mode === "edit";
      const url = isEdit 
        ? `${API}/catalogue/structures/${structureModal.data.id}`
        : `${API}/catalogue/structures`;
      
      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success(isEdit ? "Structure modifiée" : "Structure créée");
      setStructureModal({ open: false, mode: "create", data: null });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteStructure = async (id) => {
    try {
      const response = await fetch(`${API}/catalogue/structures/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success("Structure supprimée");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Offer CRUD
  const handleSaveOffer = async (formData) => {
    try {
      const isEdit = offerModal.mode === "edit";
      const url = isEdit 
        ? `${API}/catalogue/offers/${offerModal.data.id}`
        : `${API}/catalogue/offers`;
      
      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success(isEdit ? "Offre modifiée" : "Offre créée");
      setOfferModal({ open: false, mode: "create", data: null });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteOffer = async (id) => {
    try {
      const response = await fetch(`${API}/catalogue/offers/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success("Offre supprimée");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Option CRUD
  const handleSaveOption = async (formData) => {
    try {
      const isEdit = optionModal.mode === "edit";
      const url = isEdit 
        ? `${API}/catalogue/options/${optionModal.data.id}`
        : `${API}/catalogue/options`;
      
      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success(isEdit ? "Option modifiée" : "Option créée");
      setOptionModal({ open: false, mode: "create", data: null });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteOption = async (id) => {
    try {
      const response = await fetch(`${API}/catalogue/options/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success("Option supprimée");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Catalogue" />
      
      <main className="p-4 space-y-4 max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-4 h-12 rounded-2xl bg-pink-100/50">
            <TabsTrigger value="structures" className="rounded-xl data-[state=active]:bg-white text-xs sm:text-sm">
              <Castle className="w-4 h-4 mr-1" />
              Structures
            </TabsTrigger>
            <TabsTrigger value="offers" className="rounded-xl data-[state=active]:bg-white text-xs sm:text-sm">
              <PartyPopper className="w-4 h-4 mr-1" />
              Offres
            </TabsTrigger>
            <TabsTrigger value="options" className="rounded-xl data-[state=active]:bg-white text-xs sm:text-sm">
              <Sparkles className="w-4 h-4 mr-1" />
              Options
            </TabsTrigger>
            <TabsTrigger value="closures" className="rounded-xl data-[state=active]:bg-white text-xs sm:text-sm">
              <CalendarOff className="w-4 h-4 mr-1" />
              Fermetures
            </TabsTrigger>
          </TabsList>

          {/* Structures Tab */}
          <TabsContent value="structures" className="mt-4 space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={() => setStructureModal({ open: true, mode: "create", data: null })}
                className="flex-1 h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-2xl"
                data-testid="add-structure-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Ajouter une structure
              </Button>
            </div>

            {/* Bulk availability controls */}
            {structures.length > 0 && (
              <Card className="card-festive">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Power className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">
                      {structures.filter(s => s.available !== false).length}/{structures.length} disponibles
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleBulkAvailability(false)}
                      data-testid="bulk-close-btn"
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      Tout fermer
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl text-xs border-green-200 text-green-600 hover:bg-green-50"
                      onClick={() => handleBulkAvailability(true)}
                      data-testid="bulk-open-btn"
                    >
                      <Power className="w-3 h-3 mr-1" />
                      Tout ouvrir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {structures.map((structure) => (
                <Card key={structure.id} className={`card-festive overflow-hidden transition-all ${structure.available === false ? "opacity-60 ring-2 ring-red-200" : ""}`} data-testid={`structure-card-${structure.id}`}>
                  <div className="relative aspect-square bg-gradient-to-br from-festive-pink/20 to-festive-purple/20">
                    {structure.image && (
                      <img
                        src={getImageUrl(structure.image)}
                        alt={structure.name}
                        className={`w-full h-full object-cover ${structure.available === false ? "grayscale" : ""}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {structure.available === false && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
                          Indisponible
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="w-8 h-8 rounded-lg bg-festive-purple text-white hover:bg-festive-purple/80 shadow-md"
                        onClick={() => setStructureModal({ open: true, mode: "edit", data: structure })}
                        data-testid={`edit-structure-${structure.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="icon"
                            variant="secondary"
                            className="w-8 h-8 rounded-lg bg-red-500 text-white hover:bg-red-600 shadow-md"
                            data-testid={`delete-structure-${structure.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer {structure.name} ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. La structure sera supprimée du catalogue.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteStructure(structure.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <h3 className="font-heading font-bold text-lg">{structure.name}</h3>
                      <div className="flex items-center gap-1.5 shrink-0" data-testid={`toggle-availability-${structure.id}`}>
                        <span className={`text-xs ${structure.available === false ? "text-red-500" : "text-green-600"}`}>
                          {structure.available === false ? "Fermé" : "Ouvert"}
                        </span>
                        <Switch
                          checked={structure.available !== false}
                          onCheckedChange={() => handleToggleAvailability(structure.id)}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 mt-1">
                      <Ruler className="w-4 h-4" />
                      <span className="text-sm">{structure.dimensions}</span>
                    </div>
                    {(structure.price_half_day > 0 || structure.price_full_day > 0) && (
                      <div className="flex gap-3 mt-2">
                        {structure.price_half_day > 0 && (
                          <span className="text-sm font-medium text-festive-pink">½ jour: {structure.price_half_day}€</span>
                        )}
                        {structure.price_full_day > 0 && (
                          <span className="text-sm font-medium text-festive-purple">Journée: {structure.price_full_day}€</span>
                        )}
                      </div>
                    )}
                    {structure.description && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{structure.description}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers" className="mt-4 space-y-4">
            <Button
              onClick={() => setOfferModal({ open: true, mode: "create", data: null })}
              className="w-full h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-2xl"
              data-testid="add-offer-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter une offre
            </Button>

            <div className="space-y-3">
              {offers.map((offer) => (
                <Card key={offer.id} className="card-festive" data-testid={`offer-card-${offer.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-semibold">{offer.name}</h3>
                          {offer.requires_validation && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                              Validation requise
                            </span>
                          )}
                          {(!offer.included_structures || offer.included_structures.length === 0) && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                              ⚠️ Aucune structure
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{offer.description}</p>
                        {offer.included_structures && offer.included_structures.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {offer.included_structures.map(inc => {
                              const struct = structures.find(s => s.id === inc.structure_id);
                              return struct ? (
                                <span 
                                  key={inc.structure_id}
                                  className={`text-xs px-2 py-0.5 rounded-full ${
                                    inc.supplement > 0 
                                      ? 'bg-orange-100 text-orange-700' 
                                      : 'bg-green-100 text-green-700'
                                  }`}
                                >
                                  {struct.name}{inc.supplement > 0 ? ` (+${inc.supplement}€)` : ''}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-xl text-festive-pink">{offer.price}€</span>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8 rounded-lg"
                          onClick={() => setOfferModal({ open: true, mode: "edit", data: offer })}
                          data-testid={`edit-offer-${offer.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-8 h-8 rounded-lg text-red-500 hover:text-red-600"
                              data-testid={`delete-offer-${offer.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette offre ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteOffer(offer.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Options Tab */}
          <TabsContent value="options" className="mt-4 space-y-4">
            <Button
              onClick={() => setOptionModal({ open: true, mode: "create", data: null })}
              className="w-full h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-2xl"
              data-testid="add-option-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter une option
            </Button>

            <div className="grid gap-3 md:grid-cols-2">
              {options.map((option) => (
                <Card key={option.id} className="card-festive" data-testid={`option-card-${option.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{option.name}</h3>
                        {option.description && (
                          <p className="text-sm text-gray-500">{option.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-festive-pink">{option.price}€</span>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8 rounded-lg"
                          onClick={() => setOptionModal({ open: true, mode: "edit", data: option })}
                          data-testid={`edit-option-${option.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-8 h-8 rounded-lg text-red-500 hover:text-red-600"
                              data-testid={`delete-option-${option.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette option ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteOption(option.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Closures Tab */}
          <TabsContent value="closures" className="mt-4 space-y-4">
            <Button
              onClick={() => setClosureModal({ open: true, mode: "create", data: null })}
              className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-700 hover:opacity-90 text-white rounded-2xl"
              data-testid="add-closure-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Programmer une fermeture
            </Button>

            {closurePeriods.length === 0 ? (
              <Card className="card-festive">
                <CardContent className="p-8 text-center text-gray-500">
                  <CalendarOff className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Aucune fermeture programmée</p>
                  <p className="text-sm mt-1">Programmez des périodes de fermeture pour vos congés ou maintenances.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {closurePeriods.map((period) => {
                  const today = new Date().toISOString().split("T")[0];
                  const isActive = period.start_date <= today && period.end_date >= today;
                  const isPast = period.end_date < today;
                  const isFuture = period.start_date > today;
                  const scopeLabel = !period.structure_ids?.length
                    ? "Toutes les structures"
                    : period.structure_ids.map(sid => structures.find(s => s.id === sid)?.name || sid).join(", ");

                  return (
                    <Card key={period.id} className={`card-festive overflow-hidden ${isPast ? "opacity-50" : ""}`} data-testid={`closure-card-${period.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {isActive && <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">EN COURS</span>}
                              {isFuture && <span className="inline-block bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">PROGRAMMÉ</span>}
                              {isPast && <span className="inline-block bg-gray-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">TERMINÉ</span>}
                              {period.reason && <span className="text-sm font-medium text-gray-700">{period.reason}</span>}
                            </div>
                            <p className="text-sm font-semibold">
                              Du {new Date(period.start_date).toLocaleDateString("fr-FR")} au {new Date(period.end_date).toLocaleDateString("fr-FR")}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {scopeLabel}
                            </p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="icon"
                              variant="secondary"
                              className="w-8 h-8 rounded-lg bg-blue-500 text-white hover:bg-blue-600 shadow-sm"
                              onClick={() => setClosureModal({ open: true, mode: "edit", data: period })}
                              data-testid={`edit-closure-${period.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="secondary" className="w-8 h-8 rounded-lg bg-red-500 text-white hover:bg-red-600 shadow-sm" data-testid={`delete-closure-${period.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer cette période ?</AlertDialogTitle>
                                  <AlertDialogDescription>Les structures concernées redeviendront disponibles pour ces dates.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteClosure(period.id)} className="bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="card-festive bg-gradient-to-br from-festive-pink/10 to-festive-purple/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-festive-yellow/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Tarifs modifiables</h4>
                <p className="text-sm text-gray-600">
                  Les modifications de tarifs s'appliquent uniquement aux nouvelles réservations.
                  Les réservations existantes conservent leurs prix d'origine.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Structure Modal */}
      <StructureModal
        open={structureModal.open}
        mode={structureModal.mode}
        data={structureModal.data}
        onClose={() => setStructureModal({ open: false, mode: "create", data: null })}
        onSave={handleSaveStructure}
      />

      {/* Offer Modal */}
      <OfferModal
        open={offerModal.open}
        mode={offerModal.mode}
        data={offerModal.data}
        onClose={() => setOfferModal({ open: false, mode: "create", data: null })}
        onSave={handleSaveOffer}
        structures={structures}
      />

      {/* Option Modal */}
      <OptionModal
        open={optionModal.open}
        mode={optionModal.mode}
        data={optionModal.data}
        onClose={() => setOptionModal({ open: false, mode: "create", data: null })}
        onSave={handleSaveOption}
      />

      {/* Closure Modal */}
      <ClosureModal
        open={closureModal.open}
        mode={closureModal.mode}
        data={closureModal.data}
        structures={structures}
        onClose={() => setClosureModal({ open: false, mode: "create", data: null })}
        onSave={handleSaveClosure}
      />

      <BottomNav />
    </div>
  );
}

// Structure Modal Component
const StructureModal = ({ open, mode, data, onClose, onSave }) => {
  const [form, setForm] = useState({ name: "", dimensions: "", description: "", image: "", price_half_day: 0, price_full_day: 0 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (data) {
      setForm({ 
        name: data.name, 
        dimensions: data.dimensions, 
        description: data.description || "", 
        image: data.image || "",
        price_half_day: data.price_half_day || 0,
        price_full_day: data.price_full_day || 0
      });
      setPreviewUrl(data.image || "");
    } else {
      setForm({ name: "", dimensions: "", description: "", image: "", price_half_day: 0, price_full_day: 0 });
      setPreviewUrl("");
    }
  }, [data, open]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Format non supporté. Utilisez JPG, PNG ou WebP");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 MB)");
      return;
    }

    // Show local preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/upload/structure-image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur d'upload");
      }

      const result = await response.json();
      setForm((prev) => ({ ...prev, image: result.url }));
      toast.success("Image importée avec succès");
    } catch (error) {
      toast.error(error.message);
      setPreviewUrl(form.image); // Revert to previous image
    } finally {
      setUploading(false);
      // Clean up local preview URL
      URL.revokeObjectURL(localPreview);
    }
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, image: "" }));
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.dimensions) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setLoading(true);
    await onSave(form);
    setLoading(false);
  };

  // Generate full URL for preview
  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Modifier la structure" : "Nouvelle structure"}</DialogTitle>
          <DialogDescription>
            {mode === "edit" ? "Modifiez les informations de la structure" : "Ajoutez une nouvelle structure au catalogue"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Image Upload */}
          <div>
            <Label>Photo de la structure</Label>
            <div className="mt-2">
              {previewUrl ? (
                <div className="relative rounded-xl overflow-hidden bg-gray-100">
                  <img
                    src={getImageUrl(previewUrl)}
                    alt="Aperçu"
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='14'%3EImage%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 w-8 h-8 rounded-full"
                    onClick={handleRemoveImage}
                    disabled={uploading}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-festive-pink hover:bg-pink-50/50 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {uploading ? (
                      <div className="w-10 h-10 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600 font-medium">Cliquez pour importer une photo</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WebP (max 5 MB)</p>
                      </>
                    )}
                  </div>
                </label>
              )}
              <input
                id="image-upload"
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              {previewUrl && !uploading && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Changer la photo
                </Button>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="name">Nom *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-festive mt-1"
              placeholder="Ex: Château Princesse"
            />
          </div>
          <div>
            <Label htmlFor="dimensions">Dimensions *</Label>
            <Input
              id="dimensions"
              value={form.dimensions}
              onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
              className="input-festive mt-1"
              placeholder="Ex: 4m x 5m"
            />
          </div>
          <div>
            <Label htmlFor="description">Description (pour le chatbot IA)</Label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full mt-1 p-3 rounded-xl border border-gray-200 focus:border-festive-pink focus:ring-2 focus:ring-festive-pink/20 transition-all resize-none"
              rows={3}
              placeholder="Ex: Idéal pour les anniversaires de 4-10 ans, thème princesse avec toboggan intégré"
              data-testid="structure-description-input"
            />
            <p className="text-xs text-gray-500 mt-1">Cette description sera utilisée par votre chatbot pour recommander cette structure</p>
          </div>
          
          {/* Pricing Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-700 mb-3">💰 Tarification (location seule)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price_half_day">Prix demi-journée (€)</Label>
                <Input
                  id="price_half_day"
                  type="number"
                  min="0"
                  value={form.price_half_day}
                  onChange={(e) => setForm({ ...form, price_half_day: parseInt(e.target.value) || 0 })}
                  className="input-festive mt-1"
                  placeholder="180"
                  data-testid="structure-price-half-day"
                />
              </div>
              <div>
                <Label htmlFor="price_full_day">Prix journée (€)</Label>
                <Input
                  id="price_full_day"
                  type="number"
                  min="0"
                  value={form.price_full_day}
                  onChange={(e) => setForm({ ...form, price_full_day: parseInt(e.target.value) || 0 })}
                  className="input-festive mt-1"
                  placeholder="280"
                  data-testid="structure-price-full-day"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Ces prix s'appliquent quand le client loue la structure sans offre/pack</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || uploading}
            className="bg-gradient-to-r from-festive-pink to-festive-purple text-white"
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Offer Modal Component
const OfferModal = ({ open, mode, data, onClose, onSave, structures }) => {
  const [form, setForm] = useState({ 
    name: "", 
    price: "", 
    description: "", 
    requires_validation: false,
    included_structures: [] 
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        name: data.name,
        price: data.price.toString(),
        description: data.description || "",
        requires_validation: data.requires_validation || false,
        included_structures: data.included_structures || [],
      });
    } else {
      // Par défaut, inclure toutes les structures avec supplément 0
      const defaultIncludes = structures.map(s => ({ structure_id: s.id, supplement: 0, included: false }));
      setForm({ name: "", price: "", description: "", requires_validation: false, included_structures: defaultIncludes });
    }
  }, [data, open, structures]);

  const toggleStructure = (structureId, included) => {
    setForm(prev => {
      const existing = prev.included_structures.find(s => s.structure_id === structureId);
      if (included) {
        if (existing) {
          return {
            ...prev,
            included_structures: prev.included_structures.map(s => 
              s.structure_id === structureId ? { ...s, included: true } : s
            )
          };
        } else {
          return {
            ...prev,
            included_structures: [...prev.included_structures, { structure_id: structureId, supplement: 0, included: true }]
          };
        }
      } else {
        return {
          ...prev,
          included_structures: prev.included_structures.map(s => 
            s.structure_id === structureId ? { ...s, included: false } : s
          )
        };
      }
    });
  };

  const updateSupplement = (structureId, supplement) => {
    setForm(prev => ({
      ...prev,
      included_structures: prev.included_structures.map(s => 
        s.structure_id === structureId ? { ...s, supplement: parseInt(supplement) || 0 } : s
      )
    }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.price) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    // Filter only included structures
    const includedStructures = form.included_structures
      .filter(s => s.included)
      .map(s => ({ structure_id: s.structure_id, supplement: s.supplement }));
    
    if (includedStructures.length === 0) {
      toast.error("Veuillez sélectionner au moins une structure compatible");
      return;
    }
    
    setLoading(true);
    await onSave({
      name: form.name,
      price: parseInt(form.price),
      description: form.description,
      requires_validation: form.requires_validation,
      included_structures: includedStructures,
    });
    setLoading(false);
  };

  // Helper to check if structure is included
  const isStructureIncluded = (structureId) => {
    const found = form.included_structures.find(s => s.structure_id === structureId);
    return found?.included || false;
  };

  const getStructureSupplement = (structureId) => {
    const found = form.included_structures.find(s => s.structure_id === structureId);
    return found?.supplement || 0;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="offer-name">Nom *</Label>
              <Input
                id="offer-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-festive mt-1"
              />
            </div>
            <div>
              <Label htmlFor="offer-price">Prix de base (€) *</Label>
              <Input
                id="offer-price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="input-festive mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="offer-desc">Description</Label>
            <textarea
              id="offer-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full mt-1 p-3 rounded-xl border border-gray-200 focus:border-festive-pink focus:ring-2 focus:ring-festive-pink/20 transition-all resize-none"
              rows={2}
            />
          </div>
          
          {/* Structures incluses */}
          <div className="border-t pt-4">
            <Label className="text-base font-semibold">Structures compatibles avec cette offre *</Label>
            <p className="text-sm text-gray-500 mb-3">Sélectionnez les structures et définissez un supplément si nécessaire</p>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {structures.map(structure => (
                <div 
                  key={structure.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    isStructureIncluded(structure.id) 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={isStructureIncluded(structure.id)}
                      onChange={(e) => toggleStructure(structure.id, e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-festive-pink focus:ring-festive-pink"
                    />
                    <span className="font-medium">{structure.name}</span>
                    <span className="text-sm text-gray-500">({structure.dimensions})</span>
                  </label>
                  
                  {isStructureIncluded(structure.id) && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Supplément:</span>
                      <Input
                        type="number"
                        min="0"
                        value={getStructureSupplement(structure.id)}
                        onChange={(e) => updateSupplement(structure.id, e.target.value)}
                        className="w-20 h-8 text-center"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {structures.length === 0 && (
              <p className="text-center text-gray-500 py-4">Aucune structure disponible. Créez d'abord des structures.</p>
            )}
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-xl bg-yellow-50 border border-yellow-200">
            <div>
              <Label htmlFor="requires-validation" className="font-medium">Validation obligatoire</Label>
              <p className="text-sm text-gray-500">Requiert une confirmation manuelle</p>
            </div>
            <Switch
              id="requires-validation"
              checked={form.requires_validation}
              onCheckedChange={(checked) => setForm({ ...form, requires_validation: checked })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-gradient-to-r from-festive-pink to-festive-purple text-white"
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Option Modal Component
const OptionModal = ({ open, mode, data, onClose, onSave }) => {
  const [form, setForm] = useState({ name: "", price: "", description: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        name: data.name,
        price: data.price.toString(),
        description: data.description || "",
      });
    } else {
      setForm({ name: "", price: "", description: "" });
    }
  }, [data, open]);

  const handleSubmit = async () => {
    if (!form.name || !form.price) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setLoading(true);
    await onSave({
      name: form.name,
      price: parseInt(form.price),
      description: form.description || null,
    });
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Modifier l'option" : "Nouvelle option"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="option-name">Nom *</Label>
            <Input
              id="option-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-festive mt-1"
            />
          </div>
          <div>
            <Label htmlFor="option-price">Prix (€) *</Label>
            <Input
              id="option-price"
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="input-festive mt-1"
            />
          </div>
          <div>
            <Label htmlFor="option-desc">Description (optionnel)</Label>
            <Input
              id="option-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="input-festive mt-1"
              placeholder="Ex: 4h / 80 gobelets"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-gradient-to-r from-festive-pink to-festive-purple text-white"
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const ClosureModal = ({ open, mode, data, structures, onClose, onSave }) => {
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    structure_ids: [],
    reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [allStructures, setAllStructures] = useState(true);

  useEffect(() => {
    if (open && mode === "edit" && data) {
      const hasSpecific = data.structure_ids?.length > 0;
      setForm({
        start_date: data.start_date || "",
        end_date: data.end_date || "",
        structure_ids: data.structure_ids || [],
        reason: data.reason || "",
      });
      setAllStructures(!hasSpecific);
    } else if (open) {
      setForm({ start_date: "", end_date: "", structure_ids: [], reason: "" });
      setAllStructures(true);
    }
  }, [open, mode, data]);

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) {
      return;
    }
    setLoading(true);
    await onSave({
      ...form,
      structure_ids: allStructures ? [] : form.structure_ids,
    });
    setLoading(false);
  };

  const toggleStructure = (id) => {
    setForm(prev => ({
      ...prev,
      structure_ids: prev.structure_ids.includes(id)
        ? prev.structure_ids.filter(s => s !== id)
        : [...prev.structure_ids, id]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Modifier la période" : "Programmer une fermeture"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="closure-reason">Motif (optionnel)</Label>
            <Input
              id="closure-reason"
              data-testid="closure-reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="input-festive mt-1"
              placeholder="Ex: Congés annuels, Maintenance..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="closure-start">Date de début</Label>
              <Input
                id="closure-start"
                data-testid="closure-start"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="input-festive mt-1"
              />
            </div>
            <div>
              <Label htmlFor="closure-end">Date de fin</Label>
              <Input
                id="closure-end"
                data-testid="closure-end"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="input-festive mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Structures concernées</Label>
            <div className="flex gap-2 mb-3">
              <Button
                type="button"
                size="sm"
                variant={allStructures ? "default" : "outline"}
                className={`rounded-xl text-xs ${allStructures ? "bg-blue-600 text-white" : ""}`}
                onClick={() => setAllStructures(true)}
                data-testid="closure-all-structures"
              >
                Toutes les structures
              </Button>
              <Button
                type="button"
                size="sm"
                variant={!allStructures ? "default" : "outline"}
                className={`rounded-xl text-xs ${!allStructures ? "bg-blue-600 text-white" : ""}`}
                onClick={() => setAllStructures(false)}
                data-testid="closure-select-structures"
              >
                Sélection
              </Button>
            </div>
            {!allStructures && (
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-xl p-3">
                {structures.map(s => (
                  <label key={s.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded-lg">
                    <input
                      type="checkbox"
                      checked={form.structure_ids.includes(s.id)}
                      onChange={() => toggleStructure(s.id)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{s.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !form.start_date || !form.end_date}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
            data-testid="closure-save-btn"
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
